import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecentActions } from '../contexts/RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { resubmitRecords } from '../services/patient/hold';
import { fetchLatestEventByType } from '../services/patient/timeline';
import { fetchRecordsPatientHeader } from '../services/patient';
import { buildRecordsPatient, type RecordsPatient } from './shared/recordsPatient';

/**
 * Manages the Upload Records modal: opening it pre-fetches the patient header
 * fields + the latest reject-records rejection reason; submit calls ExitHold
 * which resumes the engine to Records-Under-Review.
 */
export function useUploadRecordsWorkflow() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [isOpen, setIsOpen] = useState(false);
    const [patient, setPatient] = useState<RecordsPatient | null>(null);
    const [rejectionNote, setRejectionNote] = useState<string | null>(null);
    const [rejectionEventId, setRejectionEventId] = useState<string | null>(null);
    const [isLoadingNote, setIsLoadingNote] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const open = useCallback(
        async (patientId: string) => {
            setIsOpen(true);
            setIsLoadingNote(true);
            setRejectionNote(null);
            setRejectionEventId(null);
            try {
                // The records-incomplete hold can be triggered by three event types:
                // reject-records (initial review), reject-refinement-records (refinement
                // review), and records-hold-note (ODO put-on-hold flow). Fetch all three
                // and surface whichever is most recent.
                const [data, initialReject, refinementReject, holdNote] = await Promise.all([
                    fetchRecordsPatientHeader(patientId),
                    fetchLatestEventByType(patientId, 'reject-records'),
                    fetchLatestEventByType(patientId, 'reject-refinement-records'),
                    fetchLatestEventByType(patientId, 'records-hold-note')
                ]);
                setPatient(buildRecordsPatient(patientId, data));
                const event =
                    [initialReject, refinementReject, holdNote]
                        .filter((e): e is NonNullable<typeof e> => e !== null)
                        .sort(
                            (a, b) =>
                                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                        )[0] ?? null;
                if (event) {
                    setRejectionEventId(event.id);
                    const reason = event.payload?.reason;
                    setRejectionNote(
                        typeof reason === 'string' && reason.trim().length > 0 ? reason : null
                    );
                }
            } catch {
                showToast('Failed to load patient records info.', 'error');
                // Keep the modal open with whatever partial state we have; close
                // it explicitly so the GP isn't stuck on an empty modal.
                setIsOpen(false);
            } finally {
                setIsLoadingNote(false);
            }
        },
        [showToast]
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setPatient(null);
        setRejectionNote(null);
        setRejectionEventId(null);
    }, []);

    const submit = useCallback(async () => {
        if (!patient?.id) return;
        setIsSubmitting(true);
        try {
            await resubmitRecords(patient.id);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
            markActionCompleted(patient.id, 'Upload records');
            showToast('Records re-submitted for review.', 'success');
            close();
        } catch {
            showToast('Failed to submit records.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [patient, queryClient, showToast, close, markActionCompleted]);

    return {
        isOpen,
        patient,
        rejectionNote,
        rejectionEventId,
        isLoadingNote,
        isSubmitting,
        open,
        close,
        submit
    };
}
